import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    logoUrl = "logoUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoUrl]: string;
    };
}

export interface MasterFluxCookiePolicyPopupStyles {
    container?: GridContainerProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
    policyContainer?: InjectableCss;
    innerPolicyContainer?: InjectableCss;
    logoContainer?: InjectableCss;
    logo?: LazyImageProps;
}

export const masterFluxCookiePolicyPopupStyles: MasterFluxCookiePolicyPopupStyles = {
    
    container: {
        gap: 10,
        css: css`
            z-index: 1300;
            background-color: rgba(0, 0, 0, 0.4);
            touch-action: none;
            overflow: hidden;
            position: fixed;
            right: 0;
            bottom: 0;
            top: 0;
            left: 0;
            max-width: 100% !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        overflow: scroll;
                    `,
                    css`
                        overflow: scroll;
                    `,
                    css`
                        overflow: scroll;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    policyContainer: {
        css: css`
            margin: 70px auto;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            background-color: #5F5AA3;
        `
    },
    innerPolicyContainer: {
        css: css`
            height: auto;
            max-height: calc(100% - 90px);
            margin: 45px;
            max-width: 800px;
            width: 100%;
            flex: 0 1 auto;
            display: flex;
            position: relative;
            flex-direction: column;
            color: #FFFFFF;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin: 35px;
                        max-width: 260px;
                    `,
                    css`
                        margin: 35px;
                        max-width: 520px;
                    `,
                    css`
                        margin: 35px;
                        max-width: 520px;
                    `,
                    null,
                    null,
                ]);
            }}
        `
    },
    logo: {
        css: css`
            height: auto;
            img {
                width: 291px;
            }
        `
    },
    logoContainer: {
        css: css`
            margin: 30px auto;
        `
    },
    acceptGridItem: {
        width: 4,
        css: css`
           margin: 30px auto  auto auto !important;
           min-width: 350px;
        `,
    },
    acceptButton: {
        css: css`
            width: 100%;
            background-color: #FFFFFF;
            border: none;
            color: #5F5AA3;
            span {
                color: unset;
            }
            &:hover {
                background-color: #5F5AA3 !important;
                border: 1px solid #FFFFFF;
                color: #FFFFFF !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 75%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    closeGridItem: {
        width: 4,
        css: css`
           margin: 0 auto !important;
           min-width: 350px;
        `,
    },
    closeButton: {
        css: css`
            width: 100%;
            background-color: #949494;
            border: none;
            color: #FFFFFF;
            span {
                color: unset;
            }
            &:hover {
                background-color: #FFFFFF !important;
                border: none;
                color: #949494 !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 75%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
};

const styles = masterFluxCookiePolicyPopupStyles;

const MasterFluxCookiePolicyPopup = ({ id, fields }: PageProps & OwnProps) => {
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const location = useAppSelector(o => getLocation(o));
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("cookieconsent_status")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setCookie("acceptCookies", "true", { expires: 365 });
        setCookie("cookieconsent_status", "allow", { expires: 365 });
        setIsOpen(false);
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href;
    };

    const closeHandler = () => {
        setCookie("acceptCookies", "false", { expires: 365 });
        setCookie("cookieconsent_status", "deny", { expires: 365 });
        setIsOpen(false);
    };

    if(!(enableCookiePrivacyPolicyPopup && !getCookie("cookieconsent_status")) && !isOpen) {
        return null;
    }
    return (
            <GridContainer {...styles.container}>
                <StyledWrapper {...styles.policyContainer}>
                    <StyledWrapper {...styles.innerPolicyContainer}>
                        <StyledWrapper {...styles.logoContainer}>
                            <LazyImage src={fields.logoUrl} {...styles.logo}/>
                        </StyledWrapper>
                        
                        <Zone contentId={id} zoneName="cookiePolicy_PrivacyPolicy" />
                        <GridItem {...styles.acceptGridItem}>
                            <Button
                                {...styles.acceptButton}
                                onClick={acceptHandler}
                                data-test-selector="cookiePolicy_AcceptCookies"
                            >
                                {siteMessage("Accept_All_Cookies")}
                            </Button>
                        </GridItem>
                        <GridItem {...styles.closeGridItem}>
                            <Button
                                {...styles.closeButton}
                                onClick={closeHandler}
                                aria-label={translate("Close")}
                                data-test-selector="cookiePolicy_ClosePolicy"
                            >
                                {siteMessage("Essential_Cookies_Only")}
                            </Button>
                        </GridItem>
                    </StyledWrapper>
                </StyledWrapper>
            </GridContainer>
    );
};


const widgetModule: WidgetModule = {
    component: MasterFluxCookiePolicyPopup,
    definition: {
        group: "Basic",
        displayName: "Masterflux Cookies Popup",
        fieldDefinitions: [
            {
                name: fields.logoUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            }
        ]
    },
};

export default widgetModule;
